import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Typography from '@johnlewispartnership/wtr-ingredients/foundations/typography';
import {
  Snackbar,
  SnackbarProps,
} from '@johnlewispartnership/wtr-ingredients/ingredients/Snackbar';
import { openModal } from 'redux/modules/common-modal/actions';
import { youHaveAlreadyRatedRecipeModal } from 'constants/modals';
import { redirectToLogin } from 'utils/login';
import { RecipeRatings } from 'redux/modules/recipes/index.d';
import { isUserLoggedIn } from 'redux/modules/sessions/selectors';
import { useSaveNewPathForPostAuthNavigation } from 'redux/modules/login/actions/save-last-non-authentication-path';
import { trackRecipe } from 'analytics/recipes-tracking';
import { trackPreLoginInteraction } from 'analytics/preLoginInteractionsTracking';
import { postRating } from 'redux/modules/recipes/actions/rating';
import { StarRating } from '@johnlewispartnership/wtr-ingredients/ingredients/StarRating';
import { Stars } from '@johnlewispartnership/wtr-ingredients/ingredients/StarRating/Stars';
import RatingsBreakdown from './RatingsBreakdown';

import utils from '../../utils/index.scss';
import styles from './index.scss';

export interface RatingsProps {
  id: string;
  ratings: RecipeRatings;
}

type RatingLabelType = {
  [index: number | string]: string;
};

const ratingLabelObj: RatingLabelType = {
  '': '',
  1: 'Poor',
  2: 'Fair',
  3: 'Average',
  4: 'Good',
  5: 'Excellent',
};

type ResponseTypes = Exclude<SnackbarProps['type'], 'info' | 'warning' | undefined>;
const snackbarMessage: Record<ResponseTypes, string> = {
  success: 'Thanks for your rating',
  error: 'Unexpected error, please try again',
};

const openYouHaveAlreadyRatedRecipeModal = (dispatch: WtrDispatch) =>
  dispatch(
    openModal({
      ...youHaveAlreadyRatedRecipeModal,
      onCloseCallback: modalCloseOption => modalCloseOption,
    }),
  );

const performRating = (id: string, rating: number, dispatch: WtrDispatch) => {
  trackRecipe(id, {
    event: 'rate_recipe',
    rating,
  });

  return dispatch(postRating(id, rating)).catch((e: Error) => {
    let errorMessage = 'rating failed';
    if (
      e.toString() === 'Error: Forbidden' ||
      e.message === 'Forbidden' ||
      e.message.includes('Customer already rated this recipe before')
    ) {
      errorMessage = 'already rated';
      openYouHaveAlreadyRatedRecipeModal(dispatch);
    }

    trackRecipe(id, {
      event: 'recipe_error',
      recipe_error_description: e.toString(),
    });

    return Promise.reject(new Error(errorMessage));
  });
};

const Ratings = ({ id, ratings }: RatingsProps) => {
  const isLoggedIn = useSelector(isUserLoggedIn);
  const dispatch = useDispatch();
  const [snackbarType, setSnackbarType] = useState<ResponseTypes>();
  const [snackbarAutoHide, setSnackbarAutoHide] = useState<boolean>(true);
  const [rating, setRating] = useState<number>(0);
  const [ratingLabel, setRatingLabel] = useState<string>('');

  const savePath = useSaveNewPathForPostAuthNavigation();

  const onHighlight = (star: string | number) => {
    setRatingLabel(ratingLabelObj[star]);

    if (typeof star === 'number') {
      setRating(star);
    }
  };

  const submitRating = () => {
    // if rating is 0, don't track it (includes mobile behaviour when user touchcancel their rating)
    if (rating < 1) {
      return;
    }

    if (isLoggedIn) {
      if (!ratings.customerRating) {
        performRating(id, rating, dispatch)
          .then(() => setSnackbarType('success'))
          .catch((_reason: string) => {
            if (String(_reason) !== 'Error: already rated') {
              setSnackbarAutoHide(false);
              setSnackbarType('error');
            }
          });
      }
    } else {
      trackPreLoginInteraction('Sign in To Rate');
      savePath({ customSearch: '?rating' });
      redirectToLogin();
    }
  };

  return (
    <div className={styles.rating}>
      <Typography element="h2" styleAs="sectionHeading" textStyle="heading-xl">
        Rate this recipe
      </Typography>
      <div className={styles['your-rating']} aria-label="Your rating">
        <Typography element="h3" styleAs="paragraphHeading" textStyle="heading-l">
          {ratingLabel || (ratings.customerRating ? 'Your rating' : 'Select your rating')}
        </Typography>

        {ratings.customerRating ? (
          <StarRating
            rating={ratings.customerRating}
            className={utils['button-as-link']}
            starSize="large"
          />
        ) : (
          <StarRating
            rating={ratings.customerRating || 0}
            starSize="large"
            onHighlight={onHighlight}
            submitHandler={submitRating}
          />
        )}
      </div>
      <div className={styles['overall-rating']}>
        <Typography
          element="h3"
          styleAs="paragraphHeading"
          textStyle="heading-l"
        >{`Overall rating ${ratings.totalRatings ? `(${ratings.average}/5)` : ''}`}</Typography>
        {!!ratings.totalRatings && (
          <div>
            <div className={styles.overallInfo}>
              <Stars starSize="medium" rating={ratings.average as number} />
              <Typography element="span" noMargins>
                {`${ratings.totalRatings} rating${ratings.totalRatings > 1 ? 's' : ''}`}
              </Typography>
            </div>
            {ratings.totalRatings >= 2 && (
              <div className={styles.ratingBreakdown}>
                <RatingsBreakdown totalRatings={ratings.totalRatings} snapshot={ratings.snapshot} />
              </div>
            )}
          </div>
        )}
        {!ratings.totalRatings && (
          <Typography element="span" className={styles['no-ratings']}>
            No ratings for this recipe yet
          </Typography>
        )}
      </div>
      <Snackbar
        message={snackbarMessage[snackbarType || 'success']}
        type={snackbarType}
        open={!!snackbarType}
        autoHide={snackbarAutoHide}
        onClose={setSnackbarType as () => void}
      />
    </div>
  );
};

export default Ratings;
